"use client";
import { useMainMetaMap } from "@/app/components/CoreDataProvider";
import {
  buildFullPath,
  buildSubjectSlug,
  getResourceDisplayName,
  isResourceAllowedForSubject,
} from "@/lib/utils/routing";
import {
  Box,
  Button,
  Flex,
  Text,
  Title,
  useMantineTheme,
} from "@mantine/core";
import CreditCardRounded from "@mui/icons-material/CreditCardRounded";
import WorkspacePremiumRounded from "@mui/icons-material/WorkspacePremiumRounded";
import { Resources } from "@tsTypes/subject-schema";
import { useSession } from "next-auth/react";
import Link from "next/link";
import WaveBanner from "../../../../components/footer/components/WaveBanner";
import classes from "./SubjectFooterLinks.module.css";
import SubjectCard from "./SubjectCard";

export default function SubjectFooterLinks({
  subjectName,
  currentResource,
}: {
  subjectName: string;
  currentResource?: Resources;
}) {
  const { data: session } = useSession();
  const subjectMeta = useMainMetaMap();
  const theme = useMantineTheme();
  let msg = "Get stuck into one of our other subjects!";
  let msg2 =
    "Join 85,000 students, across 130+ countries, in 500+ IB schools. That's half of the IB science graduates worldwide.";
  let btnTitle = "Start a 7d free trial";
  let btnLink = "/register";
  let btnColor = "darkblue";
  let BtnIcon = WorkspacePremiumRounded;
  if (session?.user) {
    const { role, subscriptionMeta } = session.user;
    if (role === "Admin" || role === "Super Admin") {
      msg =
        "As an admin you can view all of our content and make powerful tweaks!";
    } else if (role === "Content Creator") {
      msg =
        "As a content creator, you can view all of our content for inspiration!";
    } else if (subscriptionMeta?.status === "trialing") {
      msg = "Get stuck into one of our other subjects!";
      msg2 =
        "Add payment information before your trial ends to keep sailing through the IB!";
      btnTitle = "Add payment information";
      btnLink = "/profile/membership";
      btnColor = "incorrect";
      BtnIcon = CreditCardRounded;
    } else if (subscriptionMeta?.status === "expired") {
      msg = "Get stuck into one of our other subjects!";
      msg2 = "Restore access to resume sailing through the IB";
      btnTitle = "Purchase membership";
      btnLink = "/profile/membership";
      btnColor = "incorrect";
    } else if (subscriptionMeta?.status === "active") {
      msg = "View all Tiber Tutor resources included in your membership";
      msg2 = "Upgrade your membership to add another subject!";
      btnTitle = "Upgrade membership";
      btnLink = "/profile/membership";
    } else {
      msg = "Get stuck into one of our other subjects!";
      msg2 =
        "Join 85,000 students, across 130+ countries, in 500+ IB schools. That's half of the IB science graduates worldwide.";
      btnTitle = "Add membership";
      btnLink = "/profile/membership";
    }
  }
  return (
    <>
      <Box className={classes.wrapper}>
        <Title order={3} className={classes.title}>
          Explored IB {subjectName}
          {currentResource ? ` ${getResourceDisplayName(currentResource)}` : ""}?
        </Title>
        <Text className={classes.text}>{msg}</Text>
        <Text className={classes.cardGroupTitle}>
          {getOtherSubjectsLabel(currentResource)}
        </Text>
        <Flex justify={"space-evenly"} my={"1.5rem"} wrap={"wrap"} gap={"md"}>
          {Object.values(subjectMeta)
            .filter((m) => !("index" in m) && m.title !== subjectName)
            .map((m) => {
              return (
                <SubjectCard
                  key={m.title}
                  subjectName={m.title}
                  href={getFooterSubjectHref({
                    targetSubjectName: m.title,
                    currentResource,
                  })}
                />
              );
            })}
        </Flex>
        {session?.user.role !== "Content Creator" && (
          <>
            <Text className={classes.text2}>{msg2}</Text>
            <Button
              component={Link}
              href={btnLink}
              size="lg"
              color={btnColor}
              variant="white"
              className={classes.button}
              leftSection={<BtnIcon />}
            >
              {btnTitle}
            </Button>
          </>
        )}
      </Box>
      <WaveBanner
        mirror
        colors={{
          light: theme.colors.lightblue[8],
          dark: theme.colors.dark[6],
        }}
      />
    </>
  );
}

function getOtherSubjectsLabel(currentResource?: Resources) {
  if (!currentResource) {
    return "Other subjects";
  }
  return `Other subjects' ${getResourceDisplayName(currentResource)}`;
}

function getFooterSubjectHref({
  targetSubjectName,
  currentResource,
}: {
  targetSubjectName: string;
  currentResource?: Resources;
}) {
  const targetSubjectNameLower = targetSubjectName.toLowerCase();
  if (
    currentResource &&
    isResourceAllowedForSubject(targetSubjectNameLower, currentResource)
  ) {
    return buildFullPath({
      subject: targetSubjectNameLower,
      resource: currentResource,
    });
  }
  return buildSubjectSlug(targetSubjectNameLower);
}
